// Welle P1b: Daten des Schülerprofils aus den P1a-Endpunkten. Das Profil rechnet keine
// Note selbst — Werte kommen aus GET /api/schueler/:id/ueberblick (berechneZeugnisnote).
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../../lib/api';
import type { Gewichtung } from '../../../types';

export type NotenStatus = 'offen' | 'vorlaeufig' | 'final';
export type Trend = 'steigend' | 'gleich' | 'fallend' | null;

export interface FachNote {
  fach: string; wert: number | null; status: NotenStatus; tendenz: number | null; trend: Trend; berechnet: number | null;
  grundlage: { ka_geschrieben: number; ka_geplant: number; wochen_beobachtet: number; wochen_gesamt: number; einzelnoten: number; einzelnoten_gewicht: number };
  /** P1c: geschrieben (mit Note) und geplant (mit Datum); N2: geplant ohne Termin (mit Nummer) */
  klassenarbeiten?: { status: 'geschrieben' | 'geplant' | 'ohne_termin'; art: 'KA' | 'Test'; datum?: string; note?: number; note_id?: number; titel?: string; termin_id?: number; nummer?: number }[];
  /** Welle N2: Teilwerte, Anker (HJ1-Endnote), Gewichtung und Regeltext — alles vom Server */
  schriftlich: number | null; muendlich: number | null; muendlich_offen: boolean;
  anker: number | null; verrechnet: boolean; anker_referenz: { halbjahr: number; schuljahr: string } | null; hj1_endnote_fehlt: boolean;
  endnote: number | null;
  gewichtung: Gewichtung; regeltext: string; regel_kurz: string; offen_text: string;
  vorjahr: { schuljahr: string; halbjahr: number; zeugnisnote: number } | null;
}
export interface MitarbeitWoche {
  von: string; kw: number; ferien: boolean; ferien_name?: string | null;
  status: 'beobachtet' | 'luecke' | 'entschuldigt' | 'ferien' | 'kommend'; wert: number | null; tendenz: string | null;
  bis?: string; bemerkung?: string | null; eintraege?: number; ids?: number[];
  einzel?: { id: number; note: number; kontext: string | null; tendenz: string | null }[];
}
/** P1c: GET /api/schueler/:id/mitarbeit */
export interface MitarbeitFach {
  fach: string; schnitt: number | null; wochen_beobachtet: number; wochen_gesamt: number; anzahl_luecken: number;
  wochen: MitarbeitWoche[]; verlauf?: { von: string; kw: number; schnitt: number | null }[];
}
export interface MitarbeitAntwort { halbjahr: 1 | 2; schuljahr: string; von: string; bis: string; faecher: MitarbeitFach[] }
/** P1c: Gespräch aus GET /api/gespraeche */
export interface Gespraech {
  id: number; lehrer_id: number; datum: string; typ: string; art: string; teilnehmer: string; anlass: string | null; inhalt: string | null;
  vereinbarung: string | null; follow_up_datum: string | null; nachfassen_am: string | null; vertraulich: number;
  teilnehmer_liste: { art: 'eltern' | 'schueler' | 'sonstige'; eltern_id?: number; name: string }[];
  anlaesse: string[];
  vereinbarungen: { id: number; text: string; wer: string | null; bis_datum: string | null; status: 'offen' | 'erledigt'; erledigt_am: string | null }[];
  eigen?: boolean; von?: string | null;
  termin_id?: number | null; // Welle B2: Eltern-Termin, aus dem das Gespräch dokumentiert wurde
}
export interface Chip { art: 'paragraph68' | 'attest_offen' | 'attestpflicht' | 'termin'; text: string; datum?: string; anzahl?: number; seit?: string | null }
export interface StehtAnEintrag {
  art: 'paragraph68' | 'attest' | 'nachfassen' | 'offener_punkt' | 'klassenarbeit' | 'klassen_aktion' | 'entschuldigung_fehlt' | 'elterntermin' | 'gespraech_doku';
  titel: string; detail: string | null; faellig_am: string | null; ueberfaellig: boolean;
  dringlichkeit: 'hoch' | 'mittel' | 'normal'; aktion: string;
  ziel: { tab?: string; gespraech_id?: number; task_id?: number | null; offener_punkt_id?: number; termin_id?: number; datum?: string; klasse_id?: number; aktion_id?: number; notiz?: string | null };
  /** Welle B4: Eltern-Termin in den nächsten 3 Tagen → zusätzlich „Blatt fürs Gespräch“ */
  blatt?: boolean;
}
export interface ElternKontakt {
  id: number; eltern_schueler_id: number; notiz: string | null; name: string | null;
  rolle: 'mutter' | 'vater' | 'grosseltern' | 'betreuer' | 'unbekannt'; hauptkontakt: boolean;
  email: string | null; telefon: string | null; letzter_kontakt: { datum: string; art: 'mail' | 'gespraech' | 'telefonat' } | null;
}
export interface Ueberblick {
  kopf: { schueler_id: number; name: string; vorname: string; nachname: string; klasse: string | null; klasse_id: number | null; faecher_bei_mir: string[]; chips: Chip[] };
  halbjahr: { halbjahr: 1 | 2; schuljahr: string; von: string; bis: string };
  fehlzeiten: { schuljahr: string; unentschuldigt: number; entschuldigt: number; davon_attest: number; schwellen: { gelb: number; rot: number } };
  noten: FachNote[];
  mitarbeit: { fach: string; schnitt: number | null; anzahl_luecken: number; wochen: MitarbeitWoche[] }[];
  naechster_termin: { art: string; datum: string; zeit: string | null; titel: string; termin_id?: number } | null;
  /** Welle P1d: Kurzfassung für die Bereich-Zeilen am Handy (gleiche Sichtbarkeit wie die Bereich-Seiten) */
  bereiche?: { gespraeche: { anzahl: number; im_schuljahr: number; zuletzt: string | null; nachfassen_am: string | null; naechster_termin?: { datum: string; zeit: string | null } | null;
    /** Welle B2: Termine, zu denen sich ein Gespräch dokumentieren lässt */
    termine_offen?: TerminOffen[] }; notizen: { im_schuljahr: number; schuljahr: string } };
  eltern: ElternKontakt[];
  steht_an: StehtAnEintrag[];
  /** Welle B2: Fächer mit gespeichertem Zeugnis-Text in diesem Halbjahr (eigene) */
  zeugnis_texte?: string[];
}
/** Welle B2: bestätigter Eltern-Termin ohne Gespräch (vergangen oder kommend) */
export interface TerminOffen { termin_id: number; datum: string; zeit: string | null; typ: 'vor_ort' | 'telefonisch' }
/** Welle B2: Vorlage für „Gespräch dokumentieren“ aus einem Termin */
export interface GespraechVorlage {
  termin_id: number; schueler_id: number; datum: string; art: string;
  teilnehmer: { art: 'eltern'; eltern_id: number; name: string }[]; gespraech_id: number | null;
}

export type VerlaufTyp = 'alle' | 'fehlzeiten' | 'mitarbeit' | 'noten' | 'notizen' | 'gespraeche' | 'mails';
export interface VerlaufEintrag {
  typ: Exclude<VerlaufTyp, 'alle'>; id: number; datum: string; titel: string; detail: string | null; quelle: string | null;
  ziel: { tab?: string; mail_id?: number; [k: string]: unknown };
}
export interface VerlaufAntwort { schuljahr: string; gesamt: number; anzahl_aeltere: number; eintraege: VerlaufEintrag[] }

/** Überblick laden; fehler ≠ leer (D3c-Regel). neuLaden() lädt still nach (Daten bleiben stehen). */
export function useUeberblick(schuelerId: number) {
  const [daten, setDaten] = useState<Ueberblick | null>(null);
  const [fehler, setFehler] = useState(false);
  const lauf = useRef(0);
  const laden = useCallback(async () => {
    const nr = ++lauf.current;
    try {
      const d = await apiFetch<Ueberblick>(`/api/schueler/${schuelerId}/ueberblick`);
      if (nr === lauf.current) { setDaten(d); setFehler(false); }
    } catch {
      if (nr === lauf.current) setFehler(true);
    }
  }, [schuelerId]);
  useEffect(() => { setDaten(null); setFehler(false); laden(); }, [laden]);
  return { daten, fehler, neuLaden: laden };
}

/** Verlauf mit Filter, Schuljahr und „Weitere laden“ */
export function useVerlauf(schuelerId: number, typ: VerlaufTyp, seite = 5, schuljahr = 'laufend') {
  const [eintraege, setEintraege] = useState<VerlaufEintrag[] | null>(null);
  const [gesamt, setGesamt] = useState(0);
  const [aeltere, setAeltere] = useState(0);
  const [fehler, setFehler] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const lauf = useRef(0);
  const hole = useCallback(async (offset: number) => {
    const nr = ++lauf.current;
    setLaedt(true);
    try {
      const d = await apiFetch<VerlaufAntwort>(`/api/schueler/${schuelerId}/verlauf?typ=${typ}&limit=${seite}&offset=${offset}&schuljahr=${encodeURIComponent(schuljahr)}`);
      if (nr !== lauf.current) return;
      setEintraege(prev => (offset === 0 || !prev ? d.eintraege : [...prev, ...d.eintraege]));
      setGesamt(d.gesamt); setAeltere(d.anzahl_aeltere); setFehler(false);
    } catch {
      if (nr === lauf.current) setFehler(true);
    } finally { if (nr === lauf.current) setLaedt(false); }
  }, [schuelerId, typ, seite, schuljahr]);
  useEffect(() => { setEintraege(null); hole(0); }, [hole]);
  return {
    eintraege, gesamt, aeltere, fehler, laedt,
    weitere: () => hole(eintraege?.length ?? 0),
    neuLaden: () => hole(0),
  };
}
